import React, { useEffect, useMemo, useState } from "react";
import { X, Coffee, Save, Users } from "lucide-react";
import { toast } from "sonner";

import { tasksApi, blocksApi, assignmentsApi } from "@/lib/api";
import { addMinutes, isoNoMs } from "@/lib/format";

/**
 * Meal break creator. Creates a single Task (kind="meal") and a single
 * ScheduleBlock at the chosen time, with assignments for every selected crew
 * member.
 *
 * "Stagger" mode splits the selection into N groups and creates N back-to-back
 * meal blocks so part of the team is always working. Each stagger lasts
 * `duration_min / N` minutes by default, so the total wall-clock window is
 * unchanged.
 */
export default function MealBreakDialog({ open, project, crew, rooms, dateKey, onClose, onCreated }) {
  const [time, setTime] = useState("13:00");
  const [duration, setDuration] = useState(60);
  const [selectedCrew, setSelectedCrew] = useState(() => new Set());
  const [stagger, setStagger] = useState(1);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      // Default: all currently-available crew selected.
      const ids = new Set(
        (crew || [])
          .filter((c) => !c.unavailable_until || (dateKey && c.unavailable_until < dateKey))
          .map((c) => c.id),
      );
      setSelectedCrew(ids);
      setStagger(1);
      setDuration(60);
      setTime("13:00");
    }
  }, [open, crew, dateKey]);

  const selectedArr = useMemo(
    () => (crew || []).filter((c) => selectedCrew.has(c.id)),
    [crew, selectedCrew],
  );

  if (!open || !project || !dateKey) return null;

  const toggle = (id) => {
    setSelectedCrew((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedCrew(new Set((crew || []).map((c) => c.id)));
  const selectNone = () => setSelectedCrew(new Set());

  const create = async () => {
    if (selectedArr.length === 0) {
      toast.error("Pick at least one crew member.");
      return;
    }
    setBusy(true);
    try {
      const roomId = rooms[0]?.id;
      if (!roomId) { toast.error("Project has no rooms."); setBusy(false); return; }

      // Stagger: split selected crew into N approximately-equal groups, each
      // starting `duration / stagger` minutes after the previous.
      const groupSize = Math.ceil(selectedArr.length / stagger);
      const groupDur = Math.max(15, Math.round(duration / Math.max(1, stagger)));
      let cursor = `${dateKey}T${time}:00Z`;
      for (let g = 0; g < stagger; g++) {
        const groupCrew = selectedArr.slice(g * groupSize, (g + 1) * groupSize);
        if (groupCrew.length === 0) break;
        const groupStart = cursor;
        const groupEnd = isoNoMs(addMinutes(groupStart, groupDur));
        // 1) Task
        const taskTitle = stagger === 1 ? "Meal break" : `Meal break — group ${g + 1}`;
        const task = await tasksApi.create(project.id, {
          title: taskTitle,
          duration_min: groupDur,
          status: "planned",
          priority: "normal",
          kind: "meal",
          notes: stagger > 1 ? `${groupCrew.length} crew · staggered ${g + 1}/${stagger}` : `${groupCrew.length} crew`,
        });
        // 2) Block
        const block = await blocksApi.create(project.id, {
          task_id: task.id,
          room_id: roomId,
          start_at: groupStart,
          end_at: groupEnd,
          is_locked: false,
        });
        // 3) Assignments — in parallel
        await Promise.all(
          groupCrew.map((c) =>
            assignmentsApi.create(block.id, { crew_member_id: c.id, is_lead: false, role_on_block: "meal" })
              .catch(() => null),
          ),
        );
        cursor = groupEnd;
      }
      toast.success(
        stagger === 1
          ? `Meal break scheduled at ${time} for ${selectedArr.length} crew`
          : `Staggered meal: ${stagger} groups · ${groupDur}min each`,
      );
      onCreated && onCreated();
      onClose && onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create meal break.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="meal-break-dialog">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-bg-surface border border-line-strong shadow-2xl">
        <div className="flex items-center justify-between p-3 border-b border-line-strong bg-bg-elevated">
          <div className="flex items-center gap-2">
            <Coffee className="w-3 h-3 text-amber" />
            <div className="mono text-2xs uppercase tracking-[0.2em] text-ink-mute">MEAL BREAK</div>
            <div className="text-sm text-amber mono tabular-nums ml-2">{dateKey}</div>
          </div>
          <button onClick={onClose} className="btn-ghost" data-testid="meal-close">
            <X className="w-3 h-3" />
          </button>
        </div>

        <div className="p-4 space-y-3 border-b border-line">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <div className="mono text-2xs uppercase tracking-widest text-ink-mute mb-1">START</div>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-full mono text-sm bg-bg-elevated border border-line text-ink px-2 py-1 tabular-nums"
                data-testid="meal-time"
              />
            </div>
            <div>
              <div className="mono text-2xs uppercase tracking-widest text-ink-mute mb-1">DURATION</div>
              <input
                type="number"
                min={15}
                max={240}
                step={15}
                value={duration}
                onChange={(e) => setDuration(Math.max(15, parseInt(e.target.value, 10) || 60))}
                className="w-full mono text-sm bg-bg-elevated border border-line text-ink px-2 py-1 tabular-nums"
                data-testid="meal-duration"
              />
            </div>
            <div>
              <div className="mono text-2xs uppercase tracking-widest text-ink-mute mb-1">STAGGER</div>
              <select
                value={stagger}
                onChange={(e) => setStagger(parseInt(e.target.value, 10))}
                className="w-full mono text-sm bg-bg-elevated border border-line text-ink px-2 py-1"
                data-testid="meal-stagger"
              >
                <option value={1}>1 — all together</option>
                <option value={2}>2 — half &amp; half</option>
                <option value={3}>3 — thirds</option>
              </select>
            </div>
          </div>
          {stagger > 1 && (
            <div className="text-2xs text-ink-mute">
              Each group takes ~{Math.round(duration / stagger)}min so the team is never fully off the floor.
            </div>
          )}
        </div>

        <div className="p-4 max-h-[280px] overflow-y-auto">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-3 h-3 text-ink-mute" />
            <span className="mono text-2xs uppercase tracking-widest text-ink-mute">
              CREW · {selectedCrew.size}/{(crew || []).length} SELECTED
            </span>
            <div className="flex-1" />
            <button onClick={selectAll} className="btn-ghost px-2 py-0.5" data-testid="meal-select-all">ALL</button>
            <button onClick={selectNone} className="btn-ghost px-2 py-0.5" data-testid="meal-select-none">NONE</button>
          </div>
          <ul className="grid grid-cols-2 gap-1">
            {(crew || []).map((c) => {
              const isNA = c.unavailable_until && dateKey && c.unavailable_until >= dateKey;
              const checked = selectedCrew.has(c.id);
              return (
                <li key={c.id}>
                  <label
                    className={`flex items-center gap-2 px-2 py-1 text-xs cursor-pointer hover:bg-bg-elevated/60 ${isNA ? "opacity-50" : ""}`}
                    title={isNA ? `Unavailable until ${c.unavailable_until}` : ""}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(c.id)}
                      className="accent-amber"
                      data-testid={`meal-crew-${c.id}`}
                    />
                    <span className="text-ink truncate">{c.name}</span>
                    {isNA && <span className="mono text-2xs text-amber ml-auto">NA</span>}
                  </label>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="p-3 border-t border-line-strong bg-bg-elevated flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost" data-testid="meal-cancel">CANCEL</button>
          <button
            onClick={create}
            disabled={busy || selectedCrew.size === 0}
            className="btn-primary px-3 py-1 mono text-2xs uppercase tracking-widest disabled:opacity-50"
            data-testid="meal-create"
          >
            <Save className="w-3 h-3 inline mr-1" /> SCHEDULE MEAL
          </button>
        </div>
      </div>
    </div>
  );
}
